import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { adminService, OpenTasksAction, User } from "../../../services/adminService";
import { taskService, TaskPerson } from "../../../services/taskService";
import { apiErrorMessage, personName } from "../../tasks/taskUtils";

interface DeactivateUserDialogProps {
    user: User | null;
    onClose: () => void;
    onDone: (user: User, tasksMoved: number) => void;
}

/** Cut someone off without losing what they did: no sign-in, any open
 * session ends on its next request, and their open tasks can be handed on. */
export const DeactivateUserDialog: React.FC<DeactivateUserDialogProps> = ({ user, onClose, onDone }) => {
    const [note, setNote] = useState("");
    const [openTasks, setOpenTasks] = useState<number | null>(null);
    const [team, setTeam] = useState<TaskPerson[]>([]);
    // "keep", "unassign", or the user id to hand them to.
    const [handOff, setHandOff] = useState<string>("unassign");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const name = user ? personName({ display_name: user.display_name ?? null, email: user.email }) : "";

    useEffect(() => {
        if (!user) return;
        setNote("");
        setHandOff("unassign");
        setError("");
        setOpenTasks(null);
        let cancelled = false;
        Promise.all([adminService.getUserHistory(user.user_id), taskService.people().catch(() => [] as TaskPerson[])])
            .then(([history, people]) => {
                if (cancelled) return;
                setOpenTasks(history.open_tasks);
                setTeam(people.filter((p) => p.assignable && p.user_id !== user.user_id));
            })
            .catch(() => !cancelled && setOpenTasks(0));
        return () => {
            cancelled = true;
        };
    }, [user]);

    const submit = async () => {
        if (!user) return;
        const action: OpenTasksAction = handOff === "keep" || handOff === "unassign" ? handOff : "reassign";
        setSaving(true);
        setError("");
        try {
            const res = await adminService.deactivateUser(user.user_id, {
                note: note.trim() || null,
                open_tasks: openTasks ? action : "keep",
                reassign_to_user_id: action === "reassign" ? handOff : null,
            });
            onDone(res.user, res.tasks_moved);
            onClose();
        } catch (err) {
            setError(apiErrorMessage(err, "Couldn't deactivate this account."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!user} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Deactivate {name}?</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    {user?.email}
                </Typography>
                <Box component="ul" sx={{ pl: 2.5, my: 2, "& li": { mb: 0.5 }, color: "text.primary", typography: "body2" }}>
                    <li>They can't sign in. If they're signed in now, they're signed out on their next click.</li>
                    <li>Everything they did keeps their name — orders, payments, tasks, all of it.</li>
                    <li>You can reactivate them any time; their password stays the same.</li>
                </Box>

                <TextField
                    fullWidth
                    label="Note (optional)"
                    placeholder="e.g. Left in October 2026"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    slotProps={{ htmlInput: { maxLength: 300 } }}
                    sx={{ mb: 2 }}
                />

                {openTasks === null ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                            Checking their open tasks…
                        </Typography>
                    </Stack>
                ) : openTasks > 0 ? (
                    <TextField
                        select
                        fullWidth
                        label={`Their ${openTasks} open task${openTasks === 1 ? "" : "s"}`}
                        value={handOff}
                        onChange={(e) => setHandOff(e.target.value)}
                        helperText={
                            handOff === "unassign"
                                ? "They'll show under “Unassigned” on the Tasks page."
                                : handOff === "keep"
                                  ? "They stay on their name until someone reassigns them."
                                  : "The new person is notified about each one."
                        }
                    >
                        <MenuItem value="unassign">Leave unassigned</MenuItem>
                        {team.map((p) => (
                            <MenuItem key={p.user_id} value={p.user_id} sx={{ display: "block" }}>
                                Hand to {personName(p)}
                                {/* Names can repeat (e.g. several "Admin"s) — the email tells them apart. */}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {p.email}
                                </Typography>
                            </MenuItem>
                        ))}
                        <MenuItem value="keep">Keep them assigned to {name}</MenuItem>
                    </TextField>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        They have no open tasks.
                    </Typography>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button color="warning" variant="contained" onClick={submit} disabled={saving || openTasks === null}>
                    Deactivate
                </Button>
            </DialogActions>
        </Dialog>
    );
};
